import { type JSX, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, Stack, TextField, Typography } from '@mui/material';

import { setPassword } from '../api.js';

/**
 * "Choose your password" screen, reached from the emailed `?setpw=<token>`
 * link. On success the server sets the session cookie and we hand control
 * back to the App (which refreshes /me and drops the user into their
 * fleets). The same screen serves both first-time set and reset — the token
 * carries the purpose, the UI is identical.
 */
const MIN_LEN = 10;

type View =
  | { kind: 'idle' }
  | { kind: 'busy' }
  | { kind: 'error'; message: string };

export function SetPassword(props: {
  token: string;
  /** Called after the password is accepted + the session cookie is set.
   *  `mfaRequired` is true when the account already has 2FA (e.g. a reset on
   *  a 2FA account) — the App routes to the TOTP challenge. */
  onDone: (mfaRequired: boolean) => void | Promise<void>;
  /** Invalid/expired token — App drops back to the sign-in screen. */
  onExpired: () => void;
}): JSX.Element {
  const [password, setPasswordValue] = useState('');
  const [confirm, setConfirm] = useState('');
  const [view, setView] = useState<View>({ kind: 'idle' });

  const tooShort = password.length > 0 && password.length < MIN_LEN;
  const mismatch = confirm.length > 0 && confirm !== password;
  const canSubmit =
    password.length >= MIN_LEN && confirm === password && view.kind !== 'busy';

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!canSubmit) return;
    setView({ kind: 'busy' });
    const res = await setPassword({ token: props.token, password });
    if (res.kind === 'ok') {
      await props.onDone(res.mfaRequired);
      return;
    }
    if (res.kind === 'expired-or-used') {
      props.onExpired();
      return;
    }
    if (res.kind === 'too-short') {
      setView({ kind: 'error', message: res.message });
      return;
    }
    if (res.kind === 'network') {
      setView({ kind: 'error', message: `Network: ${res.message}` });
      return;
    }
    setView({ kind: 'error', message: `Server returned ${String(res.status)}.` });
  }

  return (
    <Card variant="outlined" data-testid="set-password" sx={{ maxWidth: 460, mx: 'auto' }}>
      <CardContent>
        <Typography variant="h6" fontWeight={700}>Choose a password</Typography>
        <Typography color="text.secondary" variant="body2" sx={{ mb: 2 }}>
          At least {MIN_LEN} characters. You&apos;ll be signed in as soon as it&apos;s set.
        </Typography>

        <Box component="form" onSubmit={(e) => void handleSubmit(e)}>
          <Stack spacing={2}>
            <TextField
              label="New password"
              type="password"
              value={password}
              onChange={(e) => setPasswordValue(e.target.value)}
              required
              fullWidth
              error={tooShort}
              helperText={tooShort ? `Use at least ${String(MIN_LEN)} characters.` : ' '}
              inputProps={{ maxLength: 200, autoComplete: 'new-password', 'data-testid': 'set-password-input' }}
            />
            <TextField
              label="Confirm password"
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
              fullWidth
              error={mismatch}
              helperText={mismatch ? "Passwords don't match." : ' '}
              inputProps={{ maxLength: 200, autoComplete: 'new-password', 'data-testid': 'set-password-confirm' }}
            />
            {view.kind === 'error' && <Alert severity="error" role="alert">{view.message}</Alert>}
            <Button
              type="submit"
              variant="contained"
              disabled={!canSubmit}
              data-testid="set-password-submit"
              sx={{ py: 1 }}
            >
              {view.kind === 'busy' ? 'Setting…' : 'Set password & sign in'}
            </Button>
          </Stack>
        </Box>
      </CardContent>
    </Card>
  );
}
